import React, { useEffect, useRef, useState } from 'react';
import './WaveformVisualizer.scss';

interface WaveformVisualizerProps {
  audioStream: MediaStream | null;
  isRecording: boolean;
}

const WaveformVisualizer: React.FC<WaveformVisualizerProps> = ({ audioStream, isRecording }) => {
  const [waveformData, setWaveformData] = useState<number[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const animationFrameRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const dataArrayRef = useRef<Uint8Array | null>(null);
  const visibilityTimerRef = useRef<NodeJS.Timeout | null>(null);

  const barCount = 20; // Количество столбцов волновой формы

  useEffect(() => {
    if (!audioStream || !isRecording) {
      // Очистка при остановке записи
      setIsVisible(false);
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
        visibilityTimerRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
        analyserRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      setWaveformData(new Array(barCount).fill(0));
      return;
    }

    // Плавное появление с небольшой задержкой
    visibilityTimerRef.current = setTimeout(() => {
      setIsVisible(true);
    }, 100);

    // Инициализация AudioContext и AnalyserNode
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256; // Размер FFT для анализа частот
      analyser.smoothingTimeConstant = 0.3; // Меньше сглаживания для более быстрой реакции
      analyserRef.current = analyser;

      const source = audioContext.createMediaStreamSource(audioStream);
      source.connect(analyser);
      sourceRef.current = source;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      dataArrayRef.current = dataArray;

      // Функция обновления волновой формы
      const updateWaveform = () => {
        if (!analyserRef.current || !dataArrayRef.current || !isRecording) {
          return;
        }

        analyserRef.current.getByteFrequencyData(dataArrayRef.current);

        // Упрощаем данные для отображения
        // Используем диапазоны частот для каждого столбца, чтобы лучше распределить энергию
        const simplifiedData: number[] = [];
        const step = Math.floor(bufferLength / barCount);
        
        for (let i = 0; i < barCount; i++) {
          const startIndex = i * step;
          const endIndex = Math.min((i + 1) * step, bufferLength);
          
          // Находим максимальное значение в диапазоне частот для этого столбца
          let maxValueInRange = 0;
          let sumValue = 0;
          let count = 0;
          
          for (let j = startIndex; j < endIndex; j++) {
            const value = dataArrayRef.current[j] || 0;
            if (value > maxValueInRange) {
              maxValueInRange = value;
            }
            sumValue += value;
            count++;
          }
          
          // Используем комбинацию максимума и среднего для лучшей визуализации
          const avgValue = count > 0 ? sumValue / count : 0;
          // Взвешенное значение: 70% максимума + 30% среднего
          let value = maxValueInRange * 0.7 + avgValue * 0.3;
          
          // Усиливаем слабые сигналы для лучшей визуализации
          // Применяем нелинейное масштабирование для повышения чувствительности
          const normalized = value / 255;
          // Используем квадратный корень для более плавного усиления слабых сигналов
          const enhanced = Math.sqrt(normalized) * 1.8; // Усиление в 1.8 раза
          // Ограничиваем максимум до 1
          const finalValue = Math.min(enhanced, 1);
          simplifiedData.push(finalValue);
        }

        setWaveformData(simplifiedData);
        animationFrameRef.current = requestAnimationFrame(updateWaveform);
      };

      updateWaveform();
    } catch (error) {
      console.error('Ошибка инициализации AudioContext для волновой формы:', error);
    }

    return () => {
      if (visibilityTimerRef.current) {
        clearTimeout(visibilityTimerRef.current);
        visibilityTimerRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (sourceRef.current) {
        sourceRef.current.disconnect();
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, [audioStream, isRecording]);

  if (!isRecording || !audioStream || waveformData.length === 0) {
    return null;
  }

  return (
    <div className={`recording-waveform ${isVisible ? 'visible' : ''}`}>
      {waveformData.map((value, index) => {
        // Высота столбца от 6px до 28px в зависимости от значения (увеличен диапазон)
        // Минимальная высота увеличена для лучшей видимости даже при тихом звуке
        const minHeight = 6;
        const maxHeight = 28;
        const height = minHeight + value * (maxHeight - minHeight);
        return (
          <div
            key={index}
            className="waveform-bar"
            style={{
              height: `${height}px`,
              opacity: value > 0.05 ? 0.5 + value * 0.5 : 0.3,
            }}
          />
        );
      })}
    </div>
  );
};

export default WaveformVisualizer;

